import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { Conversation, Message, Role } from '../types';

/**
 * Reusable WhatsApp conversation panel: message history, agent reply composer,
 * and the human-handoff / block controls. Used standalone in the Inbox and
 * embedded inside the lead detail screen.
 */
export function ChatThread({
  phone,
  tenantId,
  role,
  pollMs = 10000,
}: {
  phone: string;
  tenantId?: string;
  role: Role;
  pollMs?: number;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conv, setConv] = useState<Conversation | null>(null);
  const [optedOut, setOptedOut] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const canReply = role === 'Agent' || role === 'AgencyAdmin' || role === 'SystemAdmin';

  // Show only the actual conversation (customer ↔ bot ↔ agent). System audit
  // entries (manual edits, manual quotes, knowledge gaps, etc.) are logged with
  // author 'system' and should not clutter the thread.
  const visibleMessages = messages.filter((m) => m.author !== 'system');

  const load = useCallback(() => {
    api
      .getConversation(phone, tenantId)
      .then((r) => {
        setMessages(r.messages);
        setConv(r.conversation);
        setOptedOut(Boolean((r.lead as { opt_out?: boolean } | null)?.opt_out));
      })
      .catch(() => undefined);
  }, [phone, tenantId]);

  useEffect(() => {
    load();
    if (!pollMs) return;
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  async function send() {
    if (!text.trim() || optedOut) return;
    setLoading(true);
    try {
      await api.reply(phone, text.trim(), tenantId);
      setText('');
      load();
    } finally {
      setLoading(false);
    }
  }

  async function toggleHandoff() {
    if (!conv) return;
    await api.handoff(phone, !conv.is_human_handling, tenantId);
    load();
  }

  async function toggleBlock() {
    if (!conv) return;
    if (!conv.is_blocked && !window.confirm(`לחסום את ${phone}? הבוט לא יגיב יותר להודעות ממספר זה.`))
      return;
    await api.setBlocked(phone, !conv.is_blocked, tenantId);
    load();
  }

  return (
    <div className="chat-thread">
      <div className="topbar" style={{ borderBottom: '1px solid var(--border)' }}>
        <strong>{phone}</strong>
        {conv?.is_human_handling && <span className="badge">נציג אנושי</span>}
        {conv?.is_blocked && <span className="badge" style={{ color: 'crimson' }}>חסום</span>}
        <div className="spacer" />
        {canReply && (
          <button className="btn secondary" onClick={toggleHandoff}>
            {conv?.is_human_handling ? 'החזר לבוט' : 'השתלט על השיחה'}
          </button>
        )}
        {canReply && (
          <button className="btn secondary" onClick={toggleBlock}>
            {conv?.is_blocked ? 'בטל חסימה' : 'חסום'}
          </button>
        )}
      </div>
      <div className="messages">
        {visibleMessages.length === 0 && <div className="empty">אין הודעות עדיין</div>}
        {visibleMessages.map((m) => (
          <div key={m.ts} className={`msg ${m.direction}`}>
            <div className="who">
              {m.direction === 'inbound' ? 'לקוח' : m.author || 'בוט'}
              {m.intent ? ` · ${m.intent}` : ''}
            </div>
            {m.text}
          </div>
        ))}
      </div>
      {optedOut && (
        <div className="lead-note warn" style={{ margin: '8px' }}>
          🚫 הלקוח הוסר מרשימת התפוצה — שליחת הודעות חסומה.
        </div>
      )}
      {canReply && (
        <div className="composer">
          <input
            value={text}
            placeholder={optedOut ? 'הלקוח הוסר מרשימת התפוצה — שליחה חסומה' : 'כתוב/י תשובה כנציג…'}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            disabled={optedOut}
          />
          <button className="btn" disabled={loading || optedOut} onClick={send}>
            שלח
          </button>
        </div>
      )}
    </div>
  );
}
